import React, { memo, useMemo, useState } from 'react';
import { Handle, Position } from 'reactflow';
import NodeSettings from './NodeSettings';
import FineTuningExamples from './FineTuningExamples';

const CustomNode = memo(({ data, id, credentials = [], tools = [] }) => {
  const { nodeInputs = {}, onNodeUpdate } = data;

  // ===== UI STATE =====
  const [isHovered, setIsHovered] = useState(false);
  const [isPromptDrawerOpen, setIsPromptDrawerOpen] = useState(false);
  const [isCredsOpen, setIsCredsOpen] = useState(false);
  const [isToolsDrawerOpen, setIsToolsDrawerOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isExamplesOpen, setIsExamplesOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // ===== EVENT HANDLERS =====
  const handleInputChange = (inputId, value, outcomeId = null) => {
    const newState = { ...nodeInputs };
    
    if (inputId === 'outcomes' && outcomeId) {
      // Special handling for outcomes (already implemented)
      const updatedOutcomes = [...(newState[inputId] || [])];
      const index = updatedOutcomes.findIndex(o => o.id === outcomeId);
      if (index >= 0) {
        updatedOutcomes[index] = { ...updatedOutcomes[index], condition: value.replace(/^\d+\.\s*/, '') };
      }
      newState[inputId] = updatedOutcomes;
    } else if (inputId === 'provider' && newState.llm) {
      // When provider changes, set appropriate model default
      const providers = {
        'OpenAI': ['GPT-4o', 'GPT-4', 'GPT-3.5-turbo', 'GPT-4o-mini'],
        'Anthropic': ['Claude 3 Opus', 'Claude 3 Sonnet', 'Claude 3 Haiku'],
        'Cohere': ['Command', 'Command Light'],
        'Grok': ['Grok-1', 'Grok-1 Mini'],
        'Mixtral': ['Mixtral 8x7B', 'Mixtral 8x22B']
      };
      
      newState[inputId] = value;
      
      // Set default model for the selected provider
      if (providers[value] && providers[value].length > 0) {
        newState.llm = providers[value][0];
      }
    } else {
      // General handling for all other inputs, including objects
      newState[inputId] = value;
    }
    
    onNodeUpdate(id, newState);
  };

  const handleSettingsUpdate = (nodeId, settings) => {
    const newState = { ...nodeInputs, settings };
    onNodeUpdate(nodeId, newState);
  };

  const handleFineTuningExamplesUpdate = (nodeId, examples) => {
    const newState = { ...nodeInputs, examples };
    onNodeUpdate(nodeId, newState);
  };

  const addOutcome = () => {
    const newOutcome = { condition: '', id: `outcome-${Date.now()}-${Math.floor(Math.random() * 1000)}` };
    const newState = {
      ...nodeInputs,
      outcomes: [...(nodeInputs.outcomes || []), newOutcome]
    };
    onNodeUpdate(id, newState);
  };

  const removeOutcome = (outcomeId) => {
    if (outcomeId === 'fallback') return;
    const newState = {
      ...nodeInputs,
      outcomes: nodeInputs.outcomes.filter(outcome => outcome.id !== outcomeId)
    };
    onNodeUpdate(id, newState);
  };

  const handleToolAdd = (tool) => {
    const currentTools = [...(nodeInputs.tools || [])];
    const toolToAdd = {
      id: tool.id,
      name: tool.name,
      description: tool.description,
      function: {
        name: tool.function_name,
        description: tool.function_description || tool.description,
        parameters: tool.function_parameters || { type: "object", properties: {} }
      },
      url: tool.url
    };
    const newState = {
      ...nodeInputs,
      tools: [...currentTools, toolToAdd]
    };
    onNodeUpdate(id, newState);
    setIsToolsDrawerOpen(false);
  };

  const handleToolRemove = (index) => {
    const updatedTools = nodeInputs.tools.filter((_, i) => i !== index);
    const newState = { ...nodeInputs, tools: updatedTools };
    onNodeUpdate(id, newState);
  };

  // ===== STYLING HELPERS =====
  const getBorderColor = () => {
    if (!data.isConnected) return '#ff4d4f';
    switch (data.status) {
      case 'processing': return '#faad14';
      case 'success': return '#52c41a';
      case 'error': return '#ff4d4f';
      default: return '#f0f0f0';
    }
  };

  const getBorderWidth = () => {
    return (data.status === 'idle' && data.isConnected) ? '1px' : '2px';
  };

  const getEmoji = () => {
    switch (data.label) {
      case 'Start': return '🚀';
      case 'Chat Completion': return '🤖';
      case 'Find and Replace': return '🔁';
      case 'Knowledge Base': return '📚';
      case 'If/Else': return '🔀';
      case 'AI Decision': return '⚖️';
      case 'Output': return '📤';
      case 'API Request': return '🔌';
      case 'Store Variable': return '💾';
      case 'Memory Manager': return '🧠';
      case 'Sentiment': return '😊';
      case 'Entity Extraction': return '🔍';
      case 'User Input': return '💬';
      default: return '';
    }
  };

  const getValueInputType = () => {
    if (data.label !== 'If/Else') return 'text';
    const operator = nodeInputs.operator || 'equals';
    return ['is greater than', 'is less than'].includes(operator) ? 'number' : 'text';
  };

  // Function to check if an input should have a credential selector
  const shouldShowCredSelector = (input) => {
    return input.id === 'apiKey' || 
           input.id === 'decisionApiKey' ||
           input.id === 'openaiApiKey' || 
           input.id === 'pineconeApiKey';
  };

  const outputs = useMemo(() => {
    if (data.label === 'AI Decision' && nodeInputs.outcomes) {
      return nodeInputs.outcomes.map(o => o.id);
    }
    return data.outputs || [];
  }, [data.label, data.outputs, nodeInputs.outcomes]);

  // Provider-specific model options
  const getModelOptions = (provider) => {
    const providers = {
      'OpenAI': ['GPT-4o', 'GPT-4', 'GPT-3.5-turbo', 'GPT-4o-mini'],
      'Anthropic': ['Claude 3 Opus', 'Claude 3 Sonnet', 'Claude 3 Haiku'],
      'Cohere': ['Command', 'Command Light'],
      'Grok': ['Grok-1', 'Grok-1 Mini'],
      'Mixtral': ['Mixtral 8x7B', 'Mixtral 8x22B']
    };
    
    return providers[provider] || providers['OpenAI'];
  };

  // Check if a setting exists
  const hasSetting = (settingName) => {
    return nodeInputs.settings && settingName in nodeInputs.settings;
  };

  // Get setting value with fallback
  const getSetting = (settingName, defaultValue = false) => {
    if (nodeInputs.settings && settingName in nodeInputs.settings) {
      return nodeInputs.settings[settingName];
    }
    return defaultValue;
  };

  return (
    <div
      style={{
        padding: '0 8px 8px 8px',
        border: `${getBorderWidth()} solid ${getBorderColor()}`,
        borderRadius: '8px',
        background: '#fff',
        width: '250px',
        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.08)',
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        position: 'relative',
        transition: 'all 0.2s ease-in-out',
        boxSizing: 'border-box',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        style={{
          padding: '10px 12px',
          borderBottom: '1px solid #f0f0f0',
          position: 'relative',
          width: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ 
          fontSize: '14px', 
          fontWeight: '600', 
          color: '#1f1f1f', 
          display: 'flex', 
          alignItems: 'center',
          maxWidth: isHovered ? 'calc(100% - 60px)' : '100%',
          transition: 'max-width 0.2s'
        }}>
          <span style={{ marginRight: '8px', flexShrink: 0 }}>{getEmoji()}</span>
          <span style={{ 
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>{data.label}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div
            style={{
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              border: `1px solid ${data.isConnected ? '#e0e0e0' : '#ff4d4f'}`,
              background: data.isConnected ? 'rgba(240, 240, 240, 0.75)' : 'rgba(255, 0, 0, 0.1)',
              opacity: data.isConnected ? 0.4 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}
          >
            <span style={{ fontSize: '10px', color: data.isConnected ? '#666' : '#ff4d4f', fontWeight: '500' }}>
              {data.isConnected ? (
                <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 448 512" height="12px" width="12px" xmlns="http://www.w3.org/2000/svg">
                  <path d="M438.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L160 338.7 393.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"></path>
                </svg>
              ) : (
                <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 16 16" height="12px" width="12px" xmlns="http://www.w3.org/2000/svg">
                  <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM8 4a.905.905 0 0 0-.9.995l.35 3.507a.552.552 0 0 0 1.1 0l.35-3.507A.905.905 0 0 0 8 4zm.002 6a1 1 0 1 0 0 2 1 1 0 0 0 0-2z"></path>
                </svg>
              )}
            </span>
          </div>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsMenuOpen(!isMenuOpen);
              }}
              style={{ 
                background: '#f9fafb', 
                color: '#64748b', 
                border: '1px solid #e0e0e0', 
                borderRadius: '4px', 
                width: '24px', 
                height: '24px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                cursor: 'pointer', 
                fontSize: '12px',
                transition: 'all 0.2s'
              }}
              title="More options"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M19 13C19.5523 13 20 12.5523 20 12C20 11.4477 19.5523 11 19 11C18.4477 11 18 11.4477 18 12C18 12.5523 18.4477 13 19 13Z" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M5 13C5.55228 13 6 12.5523 6 12C6 11.4477 5.55228 11 5 11C4.44772 11 4 11.4477 4 12C4 12.5523 4.44772 13 5 13Z" stroke="#64748b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            
            {isMenuOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '28px',
                  right: '0',
                  background: '#fff',
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                  zIndex: 1000,
                  minWidth: '120px',
                  overflow: 'hidden'
                }}
              >
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsSettingsOpen(true);
                    setIsMenuOpen(false);
                  }}
                  style={{
                    padding: '8px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    transition: 'background 0.2s',
                    fontSize: '13px',
                    color: '#1f1f1f',
                    borderBottom: '1px solid #f0f0f0'
                  }}
                  onMouseEnter={(e) => {e.currentTarget.style.background = '#f9fafb'}}
                  onMouseLeave={(e) => {e.currentTarget.style.background = '#fff'}}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="#0ea5e9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.258 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.742 9.96512 4.0113 9.77251C4.28059 9.5799 4.48572 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.95653 6.85425 4.10405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72569 4.07447C9.90337 3.80618 9.99872 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z" stroke="#0ea5e9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Settings</span>
                </div>
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    if (data.onDuplicate) {
                      console.log(`Requesting duplication of node ${id}`);
                      data.onDuplicate(id, { type: data.label, inputs: nodeInputs, nodeData: data });
                    }
                    setIsMenuOpen(false);
                  }}
                  style={{
                    padding: '8px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    transition: 'background 0.2s',
                    fontSize: '13px',
                    color: '#1f1f1f',
                    borderBottom: '1px solid #f0f0f0'
                  }}
                  onMouseEnter={(e) => {e.currentTarget.style.background = '#f9fafb'}}
                  onMouseLeave={(e) => {e.currentTarget.style.background = '#fff'}}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M16 1H4C2.9 1 2 1.9 2 3V17H4V3H16V1ZM19 5H8C6.9 5 6 5.9 6 7V21C6 22.1 6.9 23 8 23H19C20.1 23 21 22.1 21 21V7C21 5.9 20.1 5 19 5ZM19 21H8V7H19V21Z" fill="#6941c6"/>
                  </svg>
                  <span>Duplicate</span>
                </div>
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    data.onDelete && data.onDelete(id);
                    setIsMenuOpen(false);
                  }}
                  style={{
                    padding: '8px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    transition: 'background 0.2s',
                    fontSize: '13px',
                    color: '#ff4d4f'
                  }}
                  onMouseEnter={(e) => {e.currentTarget.style.background = '#fff1f0'}}
                  onMouseLeave={(e) => {e.currentTarget.style.background = '#fff'}}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="#ff4d4f"/>
                  </svg>
                  <span>Delete</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ paddingTop: '12px' }}>
        {data.label !== 'Start' && (
          <Handle type="target" position={Position.Top} style={{ background: '#555', width: '10px', height: '10px' }} />
        )}
        
        {/* If global node, show a special indicator */}
        {getSetting('globalNode') && (
          <div style={{
            position: 'absolute',
            top: '-4px',
            right: '-4px',
            background: '#6941c6',
            color: 'white',
            borderRadius: '4px',
            padding: '2px 4px',
            fontSize: '9px',
            fontWeight: 'bold',
            zIndex: 10
          }}>
            GLOBAL
          </div>
        )}
        
        {(data.label === 'If/Else' || data.label === 'Output') && (
          <div style={{ marginBottom: '10px' }}>
            <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '4px', fontWeight: '500' }}>
              Node Input
            </label>
            <input
              type="text"
              value={data.lastInput || 'N/A'}
              readOnly
              style={{
                width: '100%',
                padding: '6px 8px',
                border: '1px solid #e0e0e0',
                borderRadius: '6px',
                background: '#f9fafb',
                fontSize: '11px',
                color: '#333',
                boxSizing: 'border-box',
              }}
            />
          </div>
        )}
        
        {/* Display LLM provider and model if applicable */}
        {data.label === 'Chat Completion' && (
          <div style={{ 
            marginBottom: '10px', 
            padding: '4px 8px', 
            background: '#f0f9ff', 
            borderRadius: '6px', 
            fontSize: '11px',
            color: '#0369a1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div>
              <span style={{ fontWeight: '500' }}>
                {nodeInputs.provider || 'OpenAI'}
              </span>
              {" / "}
              <span>
                {nodeInputs.llm || 'GPT-4o'}
              </span>
            </div>
            
            {/* Settings badges */}
            <div style={{ display: 'flex', gap: '4px' }}>
              {getSetting('skipResponse') && (
                <div title="Skip Response" style={{ 
                  width: '18px', 
                  height: '18px', 
                  borderRadius: '4px', 
                  background: '#f9f5ff', 
                  color: '#6941c6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px'
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 5L21 12M21 12L14 19M21 12H3" stroke="#6941c6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}
              
              {getSetting('blockInterruptions') && (
                <div title="Block Interruptions" style={{ 
                  width: '18px', 
                  height: '18px', 
                  borderRadius: '4px', 
                  background: '#fffbeb', 
                  color: '#d97706',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px'
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M15 9L9 15" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9 9L15 15" stroke="#d97706" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}
              
              {getSetting('streamResponse') && (
                <div title="Stream Response" style={{ 
                  width: '18px', 
                  height: '18px', 
                  borderRadius: '4px', 
                  background: '#f0fdf4', 
                  color: '#16a34a',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px'
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 12H19M19 12L13 6M19 12L13 18" stroke="#16a34a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              )}
            </div>
          </div>
        )}
        
        {data.inputs && data.inputs.map((input) => {
          // Hide inputs for dynamicSelect when dependsOn is set
          if (input.type === 'dynamicSelect' && input.dependsOn && nodeInputs[input.dependsOn]) {
            // Show only if parent select has a value
            if (!nodeInputs[input.dependsOn]) {
              return null;
            }
          }
          
          // Check if input should be shown based on showIf condition
          if (input.showIf) {
            const condition = Object.entries(input.showIf)[0];
            if (condition && nodeInputs[condition[0]] !== condition[1]) {
              return null;
            }
          }
          
          return (
            <div key={input.id} style={{ marginBottom: '10px', position: 'relative' }}>
              <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '4px', fontWeight: '500' }}>
                {input.label}
                {data.label === 'Chat Completion' && input.id === 'prompt' && (
                  <button
                    onClick={() => setIsPromptDrawerOpen(true)}
                    style={{ 
                      marginLeft: '8px', 
                      padding: '2px 6px', 
                      background: '#f9f5ff', 
                      color: '#6941c6', 
                      border: '1px solid #e9d7fe', 
                      borderRadius: '4px', 
                      cursor: 'pointer', 
                      fontSize: '10px',
                      transition: 'all 0.2s'
                    }}
                  >
                    Edit
                  </button>
                )}
                {shouldShowCredSelector(input) && (
                  <button
                    onClick={() => setIsCredsOpen(!isCredsOpen)}
                    style={{ 
                      marginLeft: '8px', 
                      padding: '2px 6px', 
                      background: '#f9f5ff', 
                      color: '#6941c6', 
                      border: '1px solid #e9d7fe', 
                      borderRadius: '4px', 
                      cursor: 'pointer', 
                      fontSize: '10px',
                      transition: 'all 0.2s'
                    }}
                  >
                    Select
                  </button>
                )}
                {data.label === 'Chat Completion' && input.id === 'examples' && (
                  <button
                    onClick={() => setIsExamplesOpen(true)}
                    style={{ 
                      marginLeft: '8px', 
                      padding: '2px 6px', 
                      background: '#f9f5ff', 
                      color: '#6941c6', 
                      border: '1px solid #e9d7fe', 
                      borderRadius: '4px', 
                      cursor: 'pointer', 
                      fontSize: '10px',
                      transition: 'all 0.2s'
                    }}
                  >
                    {nodeInputs.examples?.length ? 'Edit' : 'Add'}
                  </button>
                )}
              </label>

              {/* Conditional rendering based on input type */}
              {input.type === 'custom' && input.id === 'requestConfig' ? (
                <div style={{ display: 'flex', alignItems: 'stretch' }}>
                  <select
                    value={(nodeInputs[input.id]?.method || 'GET')}
                    onChange={(e) => {
                      // Get existing values or set defaults
                      const currentMethod = e.target.value;
                      const currentUrl = nodeInputs[input.id]?.url || '';
                      
                      // Update with the new values
                      handleInputChange(input.id, { 
                        method: currentMethod, 
                        url: currentUrl 
                      });
                    }}
                    style={{ 
                      width: '90px', 
                      padding: '6px 8px', 
                      border: '1px solid #e0e0e0', 
                      borderRadius: '6px 0 0 6px', 
                      background: '#fff', 
                      fontSize: '12px', 
                      color: '#333',
                      borderRight: 'none',
                      appearance: 'none',
                      backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' xmlns='http://www.w3.org/2000/svg' fill='%23666666'%3E%3Cpath d='M0 0h10L5 6z'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 8px center',
                      paddingRight: '24px'
                    }}
                  >
                    <option value="GET">GET</option>
                    <option value="POST">POST</option>
                    <option value="PUT">PUT</option>
                    <option value="DELETE">DELETE</option>
                    <option value="PATCH">PATCH</option>
                  </select>
                  <input
                    type="text"
                    value={(nodeInputs[input.id]?.url || '')}
                    onChange={(e) => {
                      // Get existing values or set defaults
                      const currentMethod = nodeInputs[input.id]?.method || 'GET';
                      const currentUrl = e.target.value;
                      
                      // Update with the new values
                      handleInputChange(input.id, { 
                        method: currentMethod, 
                        url: currentUrl 
                      });
                    }}
                    placeholder="https://api.example.com/data"
                    style={{ 
                      flex: 1, 
                      padding: '6px 8px', 
                      border: '1px solid #e0e0e0', 
                      borderRadius: '0 6px 6px 0', 
                      fontSize: '12px', 
                      color: '#333' 
                    }}
                  />
                </div>
              ) : input.type === 'custom' && input.id === 'headers' ? (
                <div style={{ 
                  textAlign: 'left',
                  maxWidth: '100%',  // Ensure it doesn't exceed node width
                  overflowX: 'hidden', // Hide overflow
                  boxSizing: 'border-box' // Include padding in width calculation
                }}>
                  {(nodeInputs[input.id] || []).map((header, index) => (
                    <div key={index} style={{ 
                      marginBottom: '8px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '4px',   // Reduced gap
                      maxWidth: '100%',
                      flexWrap: 'nowrap' // Prevent wrapping
                    }}>
                      <input
                        type="text"
                        value={header.key || ''}
                        onChange={(e) => {
                          const updatedHeaders = [...(nodeInputs[input.id] || [])];
                          updatedHeaders[index] = { ...updatedHeaders[index], key: e.target.value };
                          handleInputChange(input.id, updatedHeaders);
                        }}
                        placeholder="Name"
                        style={{ 
                          width: '40%', // Fixed proportion
                          padding: '6px', // Reduced padding
                          border: '1px solid #e0e0e0', 
                          borderRadius: '6px', 
                          fontSize: '11px',  // Smaller font
                          color: '#333',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      />
                      <input
                        type="text"
                        value={header.value || ''}
                        onChange={(e) => {
                          const updatedHeaders = [...(nodeInputs[input.id] || [])];
                          updatedHeaders[index] = { ...updatedHeaders[index], value: e.target.value };
                          handleInputChange(input.id, updatedHeaders);
                        }}
                        placeholder="Value"
                        style={{ 
                          width: '50%', // Fixed proportion
                          padding: '6px', // Reduced padding
                          border: '1px solid #e0e0e0', 
                          borderRadius: '6px', 
                          fontSize: '11px',  // Smaller font
                          color: '#333',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                      />
                      <button
                        onClick={() => {
                          const updatedHeaders = (nodeInputs[input.id] || []).filter((_, i) => i !== index);
                          handleInputChange(input.id, updatedHeaders);
                        }}
                        style={{ 
                          background: '#fff1f0', 
                          color: '#ff4d4f', 
                          border: '1px solid #ffccc7', 
                          borderRadius: '4px', 
                          width: '20px',  // Smaller button
                          height: '20px',  // Smaller button
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          cursor: 'pointer', 
                          fontSize: '10px',  // Smaller icon
                          flexShrink: 0,
                          minWidth: '20px'
                        }}
                      >
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="#ff4d4f"/>
                        </svg>
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      const currentHeaders = nodeInputs[input.id] || [];
                      handleInputChange(input.id, [...currentHeaders, { key: '', value: '' }]);
                    }}
                    style={{ 
                      padding: '4px 8px', // Smaller padding
                      background: '#f9f5ff', 
                      color: '#6941c6', 
                      border: '1px solid #e9d7fe', 
                      borderRadius: '6px', 
                      cursor: 'pointer', 
                      fontSize: '11px', // Smaller font
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '4px',
                      transition: 'all 0.2s',
                      width: 'fit-content' // Only as wide as needed
                    }}
                  >
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="#6941c6"/>
                    </svg>
                    Add Header
                  </button>
                </div>
              ) : input.type === 'custom' && input.id === 'variableSource' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <select
                    value={nodeInputs[input.id]?.type || 'Last Message'}
                    onChange={(e) => {
                      const currentConfig = nodeInputs[input.id] || { type: 'Last Message', value: '' };
                      handleInputChange(input.id, { ...currentConfig, type: e.target.value });
                    }}
                    style={{ 
                      width: '100%', 
                      padding: '6px 8px', 
                      border: '1px solid #e0e0e0', 
                      borderRadius: '6px', 
                      background: '#fff', 
                      fontSize: '12px', 
                      color: '#333',
                      appearance: 'none',
                      backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' xmlns='http://www.w3.org/2000/svg' fill='%23666666'%3E%3Cpath d='M0 0h10L5 6z'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 8px center',
                      paddingRight: '24px'
                    }}
                  >
                    <option value="Last Message">Last Message</option>
                    <option value="Custom Value">Custom Value</option>
                    <option value="Message at Index">Message at Index</option>
                  </select>
                  
                  {(nodeInputs[input.id]?.type === 'Custom Value' || nodeInputs[input.id]?.type === 'Message at Index') && (
                    <input
                      type={nodeInputs[input.id]?.type === 'Message at Index' ? 'number' : 'text'}
                      value={nodeInputs[input.id]?.value || ''}
                      onChange={(e) => {
                        const currentConfig = nodeInputs[input.id] || { type: 'Last Message', value: '' };
                        handleInputChange(input.id, { ...currentConfig, value: e.target.value });
                      }}
                      placeholder={nodeInputs[input.id]?.type === 'Message at Index' ? 'Enter index (0, 1, 2...)' : 'Enter custom value'}
                      style={{ 
                        width: '100%', 
                        padding: '6px 8px', 
                        border: '1px solid #e0e0e0', 
                        borderRadius: '6px', 
                        fontSize: '12px', 
                        color: '#333' 
                      }}
                    />
                  )}
                </div>
              ) : input.type === 'custom' && input.id === 'advanced' ? (
                <div>
                  <button
                    onClick={() => {
                      // Toggle showing advanced options
                      const showAdvanced = nodeInputs._showAdvanced || false;
                      handleInputChange('_showAdvanced', !showAdvanced);
                    }}
                    style={{ 
                      padding: '6px 10px', 
                      background: '#f9fafb', 
                      color: '#4b5563', 
                      border: '1px solid #e0e0e0', 
                      borderRadius: '6px', 
                      cursor: 'pointer', 
                      fontSize: '12px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '4px',
                      width: '100%',
                      justifyContent: 'center'
                    }}
                  >
                    {nodeInputs._showAdvanced ? 'Hide Advanced Options' : 'Show Advanced Options'}
                    <svg 
                      width="12" 
                      height="12" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      xmlns="http://www.w3.org/2000/svg"
                      style={{ transform: nodeInputs._showAdvanced ? 'rotate(180deg)' : 'rotate(0deg)' }}
                    >
                      <path d="M6 9l6 6 6-6" stroke="#4b5563" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  
                  {nodeInputs._showAdvanced && (
                    <div style={{ 
                      marginTop: '8px', 
                      padding: '8px', 
                      background: '#f9fafb', 
                      borderRadius: '6px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px',
                      width: '100%',
                      boxSizing: 'border-box'
                    }}>
                      <div style={{ width: '100%' }}>
                        <label style={{ fontSize: '11px', color: '#666', display: 'block', marginBottom: '4px' }}>
                          Max Items
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="100"
                          value={Number(nodeInputs[input.id]?.maxItems || 10)}
                          onChange={(e) => {
                            // Get existing values or set defaults
                            const includeInConversation = 
                              nodeInputs[input.id]?.includeInConversation ?? true;
                            
                            // Update with new values
                            handleInputChange(input.id, { 
                              maxItems: Number(e.target.value),
                              includeInConversation: includeInConversation
                            });
                          }}
                          style={{ 
                            width: '100%', 
                            padding: '6px 8px', 
                            border: '1px solid #e0e0e0', 
                            borderRadius: '6px', 
                            fontSize: '12px', 
                            color: '#333',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                      
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input
                          type="checkbox"
                          checked={nodeInputs[input.id]?.includeInConversation ?? true}
                          onChange={(e) => {
                            // Get existing values or set defaults
                            const maxItems = 
                              nodeInputs[input.id]?.maxItems || 10;
                            
                            // Update with new values
                            handleInputChange(input.id, { 
                              maxItems: maxItems,
                              includeInConversation: e.target.checked
                            });
                          }}
                          id={`include-in-convo-${id}`}
                          style={{ cursor: 'pointer' }}
                        />
                        <label 
                          htmlFor={`include-in-convo-${id}`} 
                          style={{ fontSize: '11px', color: '#666', cursor: 'pointer' }}
                        >
                          Include in conversation
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              ) : input.type === 'custom' && input.id === 'outcomes' ? (
                <div style={{ textAlign: 'left' }}>
                  {nodeInputs[input.id]?.map((outcome, index) => (
                    <div key={outcome.id} style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <input
                        type="text"
                        value={outcome.condition ? `${index + 1}. ${outcome.condition}` : `${index + 1}. `}
                        onChange={(e) => handleInputChange(input.id, e.target.value, outcome.id)}
                        placeholder={outcome.id === 'fallback' ? `${index + 1}. Fallback condition` : `${index + 1}. Describe condition`}
                        readOnly={outcome.id === 'fallback'}
                        style={{ 
                          width: '100%', 
                          padding: '6px 8px', 
                          border: '1px solid #e0e0e0', 
                          borderRadius: '6px', 
                          background: outcome.id === 'fallback' ? '#f9fafb' : '#fff', 
                          fontSize: '12px', 
                          color: '#333', 
                          boxSizing: 'border-box', 
                          textAlign: 'left' 
                        }}
                      />
                      {outcome.id !== 'fallback' && (
                        <button
                          onClick={() => removeOutcome(outcome.id)}
                          style={{ 
                            background: '#fff1f0', 
                            color: '#ff4d4f', 
                            border: '1px solid #ffccc7', 
                            borderRadius: '4px', 
                            width: '24px', 
                            height: '24px', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            cursor: 'pointer', 
                            fontSize: '12px',
                            transition: 'all 0.2s'
                          }}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="#ff4d4f"/>
                          </svg>
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    onClick={addOutcome}
                    style={{ 
                      padding: '6px 10px', 
                      background: '#f9f5ff', 
                      color: '#6941c6', 
                      border: '1px solid #e9d7fe', 
                      borderRadius: '6px', 
                      cursor: 'pointer', 
                      fontSize: '12px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '4px',
                      transition: 'all 0.2s'
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="#6941c6"/>
                    </svg>
                    Add Outcome
                  </button>
                </div>
              ) : input.type === 'toolList' ? (
                <div style={{ textAlign: 'left' }}>
                  {nodeInputs[input.id]?.length > 0 ? (
                    <div style={{ marginBottom: '8px' }}>
                      {nodeInputs[input.id].map((tool, idx) => (
                        <div 
                          key={tool.id || idx} 
                          style={{ 
                            marginBottom: '8px', 
                            padding: '8px', 
                            border: '1px solid #e0e0e0', 
                            borderRadius: '6px',
                            background: '#f9fafb',
                            position: 'relative'
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                              <div style={{ fontSize: '12px', fontWeight: '600', color: '#333' }}>{tool.name}</div>
                              {tool.description && (
                                <div style={{ fontSize: '11px', color: '#666', marginTop: '2px' }}>{tool.description}</div>
                              )}
                            </div>
                            <button
                              onClick={() => handleToolRemove(idx)}
                              style={{ 
                                background: '#fff1f0', 
                                border: '1px solid #ffccc7', 
                                color: '#ff4d4f', 
                                borderRadius: '4px', 
                                width: '20px', 
                                height: '20px', 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center', 
                                cursor: 'pointer', 
                                fontSize: '10px',
                                transition: 'all 0.2s',
                                marginLeft: '8px'
                              }}
                              title="Remove tool"
                            >
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="#ff4d4f"/>
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ 
                      padding: '16px', 
                      textAlign: 'center', 
                      border: '1px dashed #e0e0e0', 
                      borderRadius: '6px', 
                      color: '#9ca3af',
                      fontSize: '12px',
                      marginBottom: '8px'
                    }}>
                      No tools selected
                    </div>
                  )}
                  <button
                    onClick={() => setIsToolsDrawerOpen(true)}
                    style={{ 
                      padding: '6px 10px', 
                      background: '#f9f5ff', 
                      color: '#6941c6', 
                      border: '1px solid #e9d7fe', 
                      borderRadius: '6px', 
                      cursor: 'pointer', 
                      fontSize: '12px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '4px',
                      transition: 'all 0.2s'
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="#6941c6"/>
                    </svg>
                    Add Tool
                  </button>
                </div>
              ) : input.type === 'examplesList' ? (
                <div style={{ textAlign: 'left' }}>
                  {nodeInputs.examples?.length > 0 ? (
                    <div 
                      style={{ 
                        padding: '8px 12px', 
                        background: '#f9f5ff', 
                        border: '1px solid #e9d7fe', 
                        borderRadius: '6px', 
                        fontSize: '12px',
                        color: '#6941c6',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'pointer'
                      }}
                      onClick={() => setIsExamplesOpen(true)}
                    >
                      <span style={{ fontWeight: '500' }}>{nodeInputs.examples.length} examples</span>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke="#6941c6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M18.5 2.5C18.8978 2.10217 19.4374 1.87868 20 1.87868C20.5626 1.87868 21.1022 2.10217 21.5 2.5C21.8978 2.89783 22.1213 3.43739 22.1213 4C22.1213 4.56261 21.8978 5.10217 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z" stroke="#6941c6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  ) : (
                    <button
                      onClick={() => setIsExamplesOpen(true)}
                      style={{ 
                        padding: '6px 10px', 
                        width: '100%',
                        background: '#f9f5ff', 
                        color: '#6941c6', 
                        border: '1px solid #e9d7fe', 
                        borderRadius: '6px', 
                        cursor: 'pointer', 
                        fontSize: '12px', 
                        display: 'flex', 
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '4px',
                        transition: 'all 0.2s'
                      }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="#6941c6"/>
                      </svg>
                      Add Examples
                    </button>
                  )}
                </div>
              ) : input.type === 'select' ? (
                <select
                  value={nodeInputs[input.id] || input.options?.[0] || ''}
                  onChange={(e) => handleInputChange(input.id, e.target.value)}
                  style={{ 
                    width: '100%', 
                    padding: '6px 8px', 
                    border: '1px solid #e0e0e0', 
                    borderRadius: '6px', 
                    background: '#fff', 
                    fontSize: '12px', 
                    color: '#333', 
                    boxSizing: 'border-box',
                    appearance: 'none',
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' xmlns='http://www.w3.org/2000/svg' fill='%23666666'%3E%3Cpath d='M0 0h10L5 6z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 8px center',
                    paddingRight: '24px'
                  }}
                >
                  {input.options?.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              ) : input.type === 'dynamicSelect' ? (
                <select
                  value={nodeInputs[input.id] || ''}
                  onChange={(e) => handleInputChange(input.id, e.target.value)}
                  style={{ 
                    width: '100%', 
                    padding: '6px 8px', 
                    border: '1px solid #e0e0e0', 
                    borderRadius: '6px', 
                    background: '#fff', 
                    fontSize: '12px', 
                    color: '#333', 
                    boxSizing: 'border-box',
                    appearance: 'none',
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='10' height='6' xmlns='http://www.w3.org/2000/svg' fill='%23666666'%3E%3Cpath d='M0 0h10L5 6z'/%3E%3C/svg%3E")`,
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'right 8px center',
                    paddingRight: '24px'
                  }}
                >
                  {input.options?.[nodeInputs[input.dependsOn] || 'OpenAI']?.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              ) : input.type === 'slider' ? (
                <div
                  onMouseDown={(e) => e.stopPropagation()}
                  onTouchStart={(e) => e.stopPropagation()}
                  style={{ 
                    marginTop: '8px',
                    marginBottom: '12px'
                  }}
                >
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    marginBottom: '10px', 
                    justifyContent: 'space-between' 
                  }}>
                    <div style={{ fontSize: '12px', fontWeight: '500', color: '#1f2937' }}>
                      {input.label}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <input
                        type="number"
                        min={input.min || 0}
                        max={input.max || 1}
                        step={input.step || 0.1}
                        value={nodeInputs[input.id] || input.default || 0}
                        onChange={(e) => {
                          e.stopPropagation();
                          const value = Math.min(Math.max(parseFloat(e.target.value), input.min || 0), input.max || 1);
                          handleInputChange(input.id, isNaN(value) ? input.default || 0 : value);
                        }}
                        onKeyDown={(e) => e.stopPropagation()}
                        style={{ 
                          width: '52px',
                          border: '1px solid #e5e7eb',
                          borderRadius: '4px',
                          padding: '3px 6px',
                          fontSize: '12px',
                          textAlign: 'center',
                          marginRight: '4px',
                          color: '#1f2937',
                          appearance: 'textfield'
                        }}
                      />
                    </div>
                  </div>
                  
                  <div className="slider-container" style={{ position: 'relative', height: '24px' }}>
                    <input
                      type="range"
                      min={input.min || 0}
                      max={input.max || 1}
                      step={input.step || 0.1}
                      value={nodeInputs[input.id] || input.default || 0}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleInputChange(input.id, parseFloat(e.target.value));
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                      onTouchStart={(e) => e.stopPropagation()}
                      style={{ 
                        width: '100%',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        margin: 0,
                        appearance: 'none',
                        height: '4px',
                        borderRadius: '2px',
                        background: `linear-gradient(to right, #6941c6 0%, #6941c6 ${((nodeInputs[input.id] || input.default || 0) - (input.min || 0)) / ((input.max || 1) - (input.min || 0)) * 100}%, #e5e7eb ${((nodeInputs[input.id] || input.default || 0) - (input.min || 0)) / ((input.max || 1) - (input.min || 0)) * 100}%, #e5e7eb 100%)`,
                        cursor: 'pointer',
                        zIndex: 2
                      }}
                      className="custom-range-input"
                    />
                    <div style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      position: 'absolute',
                      width: '100%',
                      top: '10px',
                      pointerEvents: 'none',
                      paddingLeft: '2px',
                      paddingRight: '2px'
                    }}>
                      <span style={{ fontSize: '10px', color: '#6b7280' }}>{input.min || 0}</span>
                      <span style={{ fontSize: '10px', color: '#6b7280' }}>{input.max || 1}</span>
                    </div>
                  </div>
                  
                  {input.description && (
                    <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '8px' }}>
                      {input.description}
                    </div>
                  )}
                  
                  <style>
                    {`
                    .custom-range-input::-webkit-slider-thumb {
                      appearance: none;
                      width: 16px;
                      height: 16px;
                      border-radius: 50%;
                      background: #6941c6;
                      border: 2px solid #fff;
                      cursor: pointer;
                      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                      transition: background 0.15s ease;
                      margin-top: -6px;
                      z-index: 3;
                    }
                    
                    .custom-range-input::-moz-range-thumb {
                      width: 16px;
                      height: 16px;
                      border-radius: 50%;
                      background: #6941c6;
                      border: 2px solid #fff;
                      cursor: pointer;
                      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
                      transition: background 0.15s ease;
                      z-index: 3;
                    }
                    
                    .custom-range-input::-webkit-slider-thumb:hover,
                    .custom-range-input:active::-webkit-slider-thumb {
                      background: #5a32b0;
                    }
                    
                    .custom-range-input::-moz-range-thumb:hover,
                    .custom-range-input:active::-moz-range-thumb {
                      background: #5a32b0;
                    }
                    
                    input[type=number]::-webkit-inner-spin-button, 
                    input[type=number]::-webkit-outer-spin-button { 
                      -webkit-appearance: none;
                      margin: 0;
                    }
                    `}
                  </style>
                </div>
              ) : input.type === 'textarea' ? (
                <textarea
                  rows={2}
                  style={{ 
                    width: '100%', 
                    padding: '6px 8px', 
                    border: '1px solid #e0e0e0', 
                    borderRadius: '6px', 
                    background: '#fff', 
                    fontSize: '12px', 
                    color: '#333', 
                    resize: 'vertical', 
                    boxSizing: 'border-box',
                    fontFamily: 'inherit'
                  }}
                  value={nodeInputs[input.id] || ''}
                  onChange={(e) => handleInputChange(input.id, e.target.value)}
                  placeholder={input.placeholder || ''}
                />
              ) : (
                /* For all other input types that are not custom */
                <input
                  type={input.id === 'value' ? getValueInputType() : input.type}
                  min={input.min}
                  max={input.max}
                  step={input.step}
                  value={nodeInputs[input.id] || ''}
                  onChange={(e) => handleInputChange(input.id, e.target.value)}
                  placeholder={input.placeholder || ''}
                  style={{ 
                    width: '100%', 
                    padding: '6px 8px', 
                    border: '1px solid #e0e0e0', 
                    borderRadius: '6px', 
                    background: '#fff', 
                    fontSize: '12px', 
                    color: '#333', 
                    boxSizing: 'border-box' 
                  }}
                />
              )}
              {isCredsOpen && shouldShowCredSelector(input) && (
                <div
                  style={{ 
                    position: 'absolute', 
                    top: '28px', 
                    right: '0', 
                    width: '180px', 
                    background: '#fff', 
                    border: '1px solid #e0e0e0', 
                    borderRadius: '6px', 
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', 
                    zIndex: 1002, 
                    padding: '8px',
                    overflow: 'hidden'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px', paddingBottom: '6px', borderBottom: '1px solid #f0f0f0' }}>
                    <span style={{ fontSize: '12px', fontWeight: '600', color: '#333' }}>Select Credential</span>
                    <button
                      onClick={() => setIsCredsOpen(false)}
                      style={{ 
                        background: 'none', 
                        border: 'none', 
                        color: '#666', 
                        cursor: 'pointer', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        padding: '2px'
                      }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="#666"/>
                      </svg>
                    </button>
                  </div>
                  {(!credentials || credentials.length === 0) ? (
                    <div style={{ fontSize: '12px', color: '#666', padding: '8px 0', textAlign: 'center' }}>No credentials available</div>
                  ) : (
                    credentials.map((cred) => (
                      <div
                        key={cred.id}
                        onClick={() => {
                          handleInputChange(input.id, cred.value);
                          setIsCredsOpen(false);
                        }}
                        style={{ 
                          padding: '8px 10px', 
                          fontSize: '12px', 
                          color: '#333', 
                          cursor: 'pointer', 
                          borderRadius: '4px',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => {e.currentTarget.style.background = '#f9f5ff'}}
                        onMouseLeave={(e) => {e.currentTarget.style.background = 'transparent'}}
                      >
                        {cred.name}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          );
        })}
        
        {outputs.map((output, index) => (
          <div key={output} style={{ marginTop: '10px', textAlign: 'center' }}>
            <Handle
              type="source"
              position={Position.Bottom}
              id={output}
              style={{ background: '#555', width: '10px', height: '10px', left: `${(100 / (outputs.length + 1)) * (index + 1)}%` }}
            />
            <span style={{ fontSize: '10px', color: '#666', display: 'block', marginTop: '4px' }}>
              {data.label === 'AI Decision' ? `${index + 1}. ${nodeInputs.outcomes?.find((o) => o.id === output)?.condition || output}` : output}
            </span>
          </div>
        ))}
      </div>

      {/* Node settings modal */}
      <NodeSettings
        node={{ id, data }}
        onUpdateSettings={handleSettingsUpdate}
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />

      {/* Fine-tuning examples modal */}
      {isExamplesOpen && (
        <FineTuningExamples
          nodeId={id}
          examples={nodeInputs.examples || []}
          onUpdate={handleFineTuningExamplesUpdate}
          isOpen={isExamplesOpen}
          onClose={() => setIsExamplesOpen(false)}
        />
      )}

      {/* Existing drawers */}
      {isPromptDrawerOpen && data.label === 'Chat Completion' && (
        <div
          style={{ 
            position: 'absolute', 
            top: '0', 
            right: '-320px', 
            width: '300px', 
            height: '100%', 
            background: '#fff', 
            border: '1px solid #e0e0e0', 
            borderRadius: '0 8px 8px 0', 
            boxShadow: '-4px 0 12px rgba(0, 0, 0, 0.08)', 
            zIndex: 1002, 
            padding: '12px', 
            display: 'flex', 
            flexDirection: 'column'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f1f1f' }}>Edit Prompt</div>
            <button 
              onClick={() => setIsPromptDrawerOpen(false)}
              style={{ 
                background: 'none', 
                border: 'none', 
                color: '#666', 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '4px'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="#666"/>
              </svg>
            </button>
          </div>
          <textarea
            value={nodeInputs.prompt || ''}
            onChange={(e) => handleInputChange('prompt', e.target.value)}
            style={{ 
              flex: 1, 
              padding: '10px', 
              border: '1px solid #e0e0e0', 
              borderRadius: '6px', 
              fontSize: '13px', 
              resize: 'none', 
              boxSizing: 'border-box',
              fontFamily: 'inherit',
              lineHeight: '1.5'
            }}
            placeholder="Enter your prompt here..."
          />
        </div>
      )}

      {isToolsDrawerOpen && (
        <div
          style={{ 
            position: 'absolute', 
            top: '0', 
            right: '-320px', 
            width: '300px', 
            height: '100%', 
            background: '#fff', 
            border: '1px solid #e0e0e0', 
            borderRadius: '0 8px 8px 0', 
            boxShadow: '-4px 0 12px rgba(0, 0, 0, 0.08)', 
            zIndex: 1002, 
            padding: '12px', 
            display: 'flex', 
            flexDirection: 'column',
            overflow: 'auto'
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: '#1f1f1f' }}>Select Tools</div>
            <button 
              onClick={() => setIsToolsDrawerOpen(false)} 
              style={{ 
                background: 'none', 
                border: 'none', 
                color: '#666', 
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '4px'
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="#666"/>
              </svg>
            </button>
          </div>
          {(!tools || tools.length === 0) ? (
            <div style={{ padding: '24px 16px', textAlign: 'center', color: '#9ca3af' }}>
              <div style={{ fontSize: '14px', marginBottom: '8px' }}>No tools available</div>
              <div style={{ fontSize: '12px' }}>Go to Settings to create tools</div>
            </div>
          ) : (
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {tools.map(tool => {
                const isSelected = nodeInputs.tools?.some(t => t.id === tool.id);
                return (
                  <div 
                    key={tool.id}
                    onClick={() => !isSelected && handleToolAdd(tool)}
                    style={{ 
                      padding: '10px 12px', 
                      margin: '4px 0', 
                      borderRadius: '6px', 
                      cursor: isSelected ? 'default' : 'pointer',
                      opacity: isSelected ? 0.5 : 1,
                      background: isSelected ? '#f9fafb' : 'transparent',
                      border: '1px solid',
                      borderColor: isSelected ? '#e0e0e0' : 'transparent',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#f9f5ff'; }}
                    onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ fontSize: '13px', fontWeight: '500' }}>
                          {tool.name}
                          {isSelected && <span style={{ marginLeft: '6px', color: '#9ca3af', fontSize: '11px' }}>(Selected)</span>}
                        </div>
                        {tool.description && (
                          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{tool.description}</div>
                        )}
                      </div>
                      {!isSelected && (
                        <div style={{ 
                          width: '20px', 
                          height: '20px', 
                          borderRadius: '50%', 
                          border: '1px solid #e0e0e0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 5V19M5 12H19" stroke="#6941c6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      )}
                    </div>
                    <div style={{ 
                      fontSize: '11px', 
                      background: '#f0f9ff', 
                      color: '#0369a1', 
                      display: 'inline-block',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      marginTop: '6px'
                    }}>
                      {tool.function_name}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <style>{`
        input:focus, textarea:focus, select:focus { 
          outline: none;
          border-color: #6941c6; 
          box-shadow: 0 0 0 2px rgba(105, 65, 198, 0.1);
        }
        button:hover {
          opacity: 0.9;
        }
      `}</style>
    </div>
  );
});

export default CustomNode;